/*
 * Đọc đơn trên POS để biết khoản cọc đã thu chưa: chỉ đọc, không ghi gì lên POS.
 * Đơn POS nối với cọc khi thu ngân ghi mã giao dịch hoặc ghi chú là mã cọc, mã đơn DH-... hoặc mã sao kê.
 * Khi có API POS báo về thì thay phần này, giữ dạng PosDepositReport.
 */
import dayjs from 'dayjs';
import { getPosOrder, getPosOrders, type PosOrderDetail } from '../../../api/PosV2';
import type { DepositRow } from './deposit';
import type { Deposit, PosDepositReport } from './quote';

const norm = (s?: string | null) => String(s ?? '').trim().toUpperCase();
const phoneTail = (s?: string | null) => String(s ?? '').replace(/\D/g, '').slice(-9);
const isCancelled = (o: Pick<PosOrderDetail, 'pos_status'>) => norm(o.pos_status) === 'CANCELLED';

/** Đơn POS có ghi mã của khoản cọc này không */
export function orderMatchesDeposit(d: Pick<Deposit, 'code' | 'bankRef' | 'order'>, o: Pick<PosOrderDetail, 'payments' | 'note'>): boolean {
  const refs = [d.code, d.order?.code, d.bankRef].filter(Boolean).map(norm);
  if ((o.payments ?? []).some((p) => p.transaction_code && refs.includes(norm(p.transaction_code)))) return true;
  const note = norm(o.note);
  return [d.code, d.order?.code].some((k) => !!k && note.includes(norm(k)));
}

/** Cùng khách theo SĐT; thiếu SĐT một bên thì không chặn */
export const sameCustomer = (d: Pick<Deposit, 'cust'>, o: Pick<PosOrderDetail, 'customer'>) => {
  const a = phoneTail(d.cust?.phone);
  const b = phoneTail(o.customer?.phone);
  return !a || !b || a === b;
};

export function reportFromOrder(o: PosOrderDetail, now = Date.now()): PosDepositReport {
  const pays = o.payments ?? [];
  const last = pays[pays.length - 1];
  const at = dayjs(last?.created_at_iso || o.created_at_iso || now).valueOf();
  return {
    posCode: o.order_code,
    finalAmount: Number(o.final_amount) || 0,
    paidAmount: Number(o.paid_amount) || 0,
    debt: Number(o.debt_amount) || 0,
    at: Number.isFinite(at) ? at : now,
    by: last?.cashier_name || undefined,
    ref: last?.transaction_code || undefined,
  };
}

export interface PosCheck { report?: PosDepositReport; cancelledCode?: string; error?: string }

/** Kế toán nhập mã đơn POS: đọc đúng đơn đó, kiểm tra đúng khách, chưa huỷ */
export async function lookupPosOrder(d: Pick<Deposit, 'cust'>, code: string): Promise<PosCheck> {
  const res = await getPosOrder(code);
  if (!res.ok || !res.data) return { error: `Không tìm thấy đơn POS ${code}` };
  if (!sameCustomer(d, res.data)) return { error: `Đơn POS ${code} là của khách khác` };
  if (isCancelled(res.data)) return { error: `Đơn POS ${code} đã huỷ` };
  return { report: reportFromOrder(res.data) };
}

/** Dò đơn POS của khoản cọc: đã gắn đơn thì đọc lại đơn đó, chưa thì tìm theo SĐT từ ngày đẩy đến nay */
export async function checkPosDeposit(r: DepositRow, now = Date.now()): Promise<PosCheck> {
  const d = r.deposit;
  const o = d.order;
  if (!o || o.posSim) return {};
  if (o.posCode) {
    const res = await getPosOrder(o.posCode);
    if (!res.ok || !res.data) return { error: res.message || `Không đọc được đơn POS ${o.posCode}` };
    return isCancelled(res.data) ? { cancelledCode: o.posCode } : { report: reportFromOrder(res.data, now) };
  }
  if (!d.cust?.phone) return {};
  const list = await getPosOrders({ search: d.cust.phone, from: dayjs(o.at).format('YYYY-MM-DD'), to: dayjs(now).format('YYYY-MM-DD'), per_page: 20 });
  if (!list.ok) return { error: list.message || 'Không đọc được đơn POS' };
  for (const it of list.data?.items ?? []) {
    if (it.status === 'cancelled' || it.pos_status === 'CANCELLED') continue;
    // eslint-disable-next-line no-await-in-loop
    const res = await getPosOrder(it.order_id);
    if (res.ok && res.data && sameCustomer(d, res.data) && orderMatchesDeposit(d, res.data)) return { report: reportFromOrder(res.data, now) };
  }
  return {};
}
